import type { StatusTone } from '../theme';

/**
 * One map for every status the app shows: project, revision, task, multicam
 * set and subscription. Each entry gives the sentence-case label people read
 * and one of five tones (see `statusTone` in theme.ts):
 *
 *   neutral   - history, or waiting on nobody in particular
 *   active    - someone is working on it
 *   attention - it's waiting on the person looking at it
 *   done      - finished well
 *   problem   - something failed
 *
 * Copper is never a status colour; it is reserved for actions and selection.
 */

export type StatusKind = 'project' | 'revision' | 'task' | 'multicam' | 'billing';

export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

const PROJECT: Record<string, StatusMeta> = {
  VIDEO_SUBMITTED: { label: 'Video submitted', tone: 'neutral' },
  EDITOR_ASSIGNED: { label: 'Editor assigned', tone: 'neutral' },
  REQUIREMENTS_REVIEW: { label: 'Requirements review', tone: 'neutral' },
  EDITING_IN_PROGRESS: { label: 'Editing in progress', tone: 'active' },
  // The creator asked for changes; the editor is working on the next cut.
  REVISION_REQUESTED: { label: 'Changes requested', tone: 'active' },
  DRAFT_READY: { label: 'Draft ready', tone: 'attention' },
  CLIENT_REVIEW: { label: 'In review', tone: 'attention' },
  FINAL_APPROVAL: { label: 'Approved', tone: 'done' },
  DELIVERED: { label: 'Delivered', tone: 'done' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  // Legacy values on some older projects.
  NEW: { label: 'Video submitted', tone: 'neutral' },
  PENDING: { label: 'Video submitted', tone: 'neutral' },
  ASSIGNED: { label: 'Editor assigned', tone: 'neutral' },
  'IN PROGRESS': { label: 'Editing in progress', tone: 'active' },
  COMPLETED: { label: 'Delivered', tone: 'done' },
};

const REVISION: Record<string, StatusMeta> = {
  UPLOADING: { label: 'Uploading', tone: 'neutral' },
  UPLOADED: { label: 'Uploaded', tone: 'neutral' },
  PROCESSING: { label: 'Processing', tone: 'active' },
  READY: { label: 'Ready for review', tone: 'attention' },
  ACCEPTED: { label: 'Approved', tone: 'done' },
  // A past verdict on an older cut, not an alarm.
  REVISION_REQUESTED: { label: 'Changes requested', tone: 'neutral' },
  FAILED: { label: 'Failed', tone: 'problem' },
};

const TASK: Record<string, StatusMeta> = {
  PENDING: { label: 'To do', tone: 'neutral' },
  IN_PROGRESS: { label: 'In progress', tone: 'active' },
  REVIEW_NEEDED: { label: 'Needs review', tone: 'attention' },
  COMPLETED: { label: 'Done', tone: 'done' },
  BLOCKED: { label: 'Blocked', tone: 'problem' },
};

const MULTICAM: Record<string, StatusMeta> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  READY: { label: 'Ready', tone: 'done' },
  LOCKED: { label: 'Submitted', tone: 'neutral' },
  SUBMITTED: { label: 'Submitted', tone: 'neutral' },
};

const BILLING: Record<string, StatusMeta> = {
  // Subscriptions
  ACTIVE: { label: 'Active', tone: 'done' },
  TRIALING: { label: 'Trial', tone: 'active' },
  // Cancelled at the period end: paid up until then.
  CANCELLING: { label: 'Ending', tone: 'attention' },
  PAST_DUE: { label: 'Payment issue', tone: 'problem' },
  GRACE_PERIOD: { label: 'Payment issue', tone: 'problem' },
  UNPAID: { label: 'Payment issue', tone: 'problem' },
  PAYMENT_FAILED: { label: 'Payment issue', tone: 'problem' },
  INCOMPLETE: { label: 'Payment incomplete', tone: 'attention' },
  CANCELED: { label: 'Cancelled', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  EXPIRED: { label: 'Ended', tone: 'neutral' },
  // Invoices
  PAID: { label: 'Paid', tone: 'done' },
  SUCCEEDED: { label: 'Paid', tone: 'done' },
  OPEN: { label: 'Due', tone: 'attention' },
  UNCOLLECTIBLE: { label: 'Not paid', tone: 'problem' },
  VOID: { label: 'Void', tone: 'neutral' },
  REFUNDED: { label: 'Refunded', tone: 'neutral' },
};

const MAPS: Record<StatusKind, Record<string, StatusMeta>> = {
  project: PROJECT,
  revision: REVISION,
  task: TASK,
  multicam: MULTICAM,
  billing: BILLING,
};

/** "SOME_VALUE" -> "Some value": readable fallback for a status we don't know yet. */
const humanize = (raw: string): string => {
  const words = raw.trim().replace(/[_-]+/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Label and tone for a status. Unknown values are shown humanized and neutral
 * rather than as raw enums; a missing status reads as an em dash.
 */
export const getStatusMeta = (kind: StatusKind, status?: string | null): StatusMeta => {
  if (!status || !String(status).trim()) return { label: '—', tone: 'neutral' };
  const key = String(status).trim().toUpperCase();
  return MAPS[kind][key] ?? { label: humanize(String(status)), tone: 'neutral' };
};
